import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChildren, QueryList, ChangeDetectionStrategy } from "@angular/core";
import { UserService, BaseComponent, Video, Lesson, ContentService, environment, HelperService, WindowService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { ClipboardService } from "ngx-clipboard";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";
import { VideoGridItemComponent } from "../video-grid-item/video-grid-item.component";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-video-rail",
  templateUrl: "video-rail.component.html",
  standalone: false,
})
export class VideoRailComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() videos: Array<Video>;
  @Input() lesson: Lesson;
  @Input() title: string;
  @Input() description: string;
  @Input() image: string;
  @Input() leftMargin: boolean = true;
  @Output() showMoreVideos: EventEmitter<void> = new EventEmitter();
  @ViewChildren(VideoGridItemComponent) videoItems: QueryList<VideoGridItemComponent>;

  public togglingAddLessonToLibrary: boolean = false;
  public copied: boolean = false;
  public isSmallViewport: boolean = false;
  public lessonRuntimeLabel: string = "";
  /** When false, only the rail summary is in tab order; press Enter to browse videos. */
  public expanded: boolean = false;
  /** Index of the video card whose options are in tab order (Enter to enter, Tab out to leave). Null = Tab moves card to card only. */
  public enteredCardIndex: number | null = null;
  /** True when the summary button has focus (for keyboard focus styling). */
  @HostBinding("class.video-rail--summary-focused") public summaryFocused: boolean = false;

  constructor(
    userService: UserService,
    public contentService: ContentService,
    public appService: AppService,
    public helperService: HelperService,
    private _clipboardService: ClipboardService,
    private _translate: TranslateService,
    private _win: WindowService
  ) {
    super(userService);
  }

  ngOnInit(): void {
    this.isSmallViewport = this.appService.isSmallViewport;
    this._updateLessonRuntimeLabel();
    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isSmallViewport = this.appService.isSmallViewport;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lesson) {
      this._updateLessonRuntimeLabel();
    }
  }

  private _updateLessonRuntimeLabel() {
    this.lessonRuntimeLabel = this.lesson?.runtime != null ? this.helperService.hhmmss(this.lesson.runtime, true) : "";
  }

  toggleSaveLesson(lesson) {
    this.togglingAddLessonToLibrary = true;
    let existingData = this.contentService.userLearnData[lesson.type == "lesson" ? "lessons" : "courses"][lesson.id];
    let saved = existingData?.enrolled ? false : true;
    let args: any = {
      user_id: this.user.id,
      learn_group_id: lesson.id,
      enrolled: saved,
    };
    if (!existingData?.date_started) args.date_started = new Date();

    this.contentService.saveUserLearnGroupData(lesson.id, args).subscribe({
      next: (result) => {
        this.togglingAddLessonToLibrary = false;
      },
      error: (error) => {
        this.appService.alertError(error);
        this.togglingAddLessonToLibrary = false;
      },
    });
  }

  copyLink(lesson) {
    this.copied = true;
    this._clipboardService.copy(environment.fenderUrl + "learn/" + lesson.type + "/" + lesson.id);
    event.stopPropagation();
    setTimeout(() => {
      this.copied = false;
    }, 1000);
  }

  /** Accessible label for the video list: rail title and video count (e.g. "Getting Started, 10 videos") */
  get railAriaLabel(): string {
    const name = this.lesson?.title ?? this.title ?? "Videos";
    const count = this.lesson ? (this.lesson.videos?.length ?? 0) : (this.videos?.length ?? 0);
    return `${name}, ${count} videos`;
  }

  /** Number of videos shown in the list (up to 10). Used for "Item X of Y" in card labels. */
  get displayedVideoCount(): number {
    const n = this.videos?.length ?? 0;
    return Math.min(n, 10);
  }

  /** Full label for the summary button: rail title, count, and hint to press Enter (for screen readers). */
  get railSummaryAriaLabel(): string {
    const hint = this._translate.instant("video.press_enter_to_browse");
    return `${this.railAriaLabel}. ${hint}`;
  }

  /** Enter the rail: put videos in tab order and focus the first. */
  enterRail(): void {
    this.expanded = true;
    this.enteredCardIndex = null;
    // Delay focus so the Enter key that activated the summary is not delivered to the first video (which would open it).
    this._win.setTimeout(() => {
      const first = this.videoItems?.first;
      if (first) first.focus();
    }, 100);
  }

  onCardEntered(index: number): void {
    this.enteredCardIndex = index;
    this._win.setTimeout(() => {
      const item = this.videoItems?.toArray()[index];
      if (item) item.focusFirstOption();
    }, 0);
  }

  onCardExited(): void {
    this.enteredCardIndex = null;
  }
}
